/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWalkthrough, WalkthroughStep } from '../../contexts/WalkthroughContext';
import { Button } from '../ui/button';
import { X, ChevronLeft, ChevronRight, Copy, Check } from 'lucide-react';
import { useChatContext } from '@shared/contexts/ChatContext';

interface TooltipPosition {
  top: number;
  left: number;
  arrowPosition: 'top' | 'bottom' | 'left' | 'right';
}

interface HighlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function WalkthroughOverlay() {
  const {
    state,
    currentStep,
    steps,
    nextStep,
    previousStep,
    endWalkthrough,
    skipWalkthrough,
  } = useWalkthrough();

  const { setExpanded } = useChatContext();
  const navigate = useNavigate();
  const location = useLocation();

  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition | null>(null);
  const [highlightRect, setHighlightRect] = useState<HighlightRect | null>(null);
  const [copied, setCopied] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Calculate tooltip position based on target element and placement
  const calculatePosition = useCallback((
    targetElement: Element,
    placement: string = 'right'
  ): TooltipPosition => {
    const rect = targetElement.getBoundingClientRect();
    const tooltipWidth = 320;
    const tooltipHeight = 200;
    const padding = 16;

    let top = 0;
    let left = 0;
    let arrowPosition: 'top' | 'bottom' | 'left' | 'right' = 'left';

    switch (placement) {
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + padding;
        arrowPosition = 'left';
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - padding;
        arrowPosition = 'right';
        break;
      case 'top':
        top = rect.top - tooltipHeight - padding;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        arrowPosition = 'bottom';
        break;
      case 'bottom':
        top = rect.bottom + padding;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        arrowPosition = 'top';
        break;
    }

    // Keep tooltip within viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (left < padding) left = padding;
    if (left + tooltipWidth > viewportWidth - padding) {
      left = viewportWidth - tooltipWidth - padding;
    }
    if (top < padding) top = padding;
    if (top + tooltipHeight > viewportHeight - padding) {
      top = viewportHeight - tooltipHeight - padding;
    }

    return { top, left, arrowPosition };
  }, []);

  // Update highlight and tooltip position
  const updatePositions = useCallback(() => {
    if (!currentStep) {
      setHighlightRect(null);
      setTooltipPosition(null);
      return;
    }

    const targetElement = document.querySelector(currentStep.targetSelector);
    if (!targetElement) {
      // Target not found, might need to wait for navigation
      setHighlightRect(null);
      setTooltipPosition(null);
      return;
    }

    // Calculate highlight rectangle
    const rect = targetElement.getBoundingClientRect();
    if (currentStep.highlight) {
      setHighlightRect({
        top: rect.top - 4,
        left: rect.left - 4,
        width: rect.width + 8,
        height: rect.height + 8,
      });
    } else {
      setHighlightRect(null);
    }

    // Calculate tooltip position
    const position = calculatePosition(targetElement, currentStep.placement);
    setTooltipPosition(position);

    // Scroll element into view if needed
    const isInViewport =
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= window.innerHeight &&
      rect.right <= window.innerWidth;

    if (!isInViewport) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentStep, calculatePosition]);

  // Auto-skip steps where target element doesn't exist (e.g., Dashboard when hidden)
  useEffect(() => {
    if (!state.isActive || !currentStep) return;

    // Give DOM time to render, then check if target exists
    const timer = setTimeout(() => {
      const targetElement = document.querySelector(currentStep.targetSelector);
      if (!targetElement) {
        // Target doesn't exist, skip to next step
        nextStep();
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [state.isActive, currentStep, nextStep]);

  // Update positions when step changes or on resize
  useEffect(() => {
    if (!state.isActive) return;

    updatePositions();

    const handleResize = () => updatePositions();
    const handleScroll = () => updatePositions();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    // Also observe DOM changes in case elements are dynamically rendered
    const observer = new MutationObserver(() => {
      setTimeout(updatePositions, 100);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
      observer.disconnect();
    };
  }, [state.isActive, currentStep, updatePositions]);

  // Handle navigation steps
  useEffect(() => {
    if (!currentStep || !currentStep.navigateTo) return;

    // Check if we're already on the correct page
    const currentPath = location.pathname;
    if (currentPath !== currentStep.navigateTo && currentStep.action === 'navigate') {
      // Don't auto-navigate, wait for user to click
    }
  }, [currentStep, location.pathname]);

  // Handle next step with navigation
  const handleNext = useCallback(() => {
    if (currentStep?.action === 'navigate' && currentStep.navigateTo) {
      navigate(currentStep.navigateTo);
      // Wait for navigation then move to next step
      setTimeout(() => {
        nextStep();
      }, 300);
    } else {
      nextStep();
    }
  }, [currentStep, navigate, nextStep]);

  // Handle copy agent prompt
  const handleCopyPrompt = useCallback(() => {
    if (currentStep?.agentPrompt) {
      navigator.clipboard.writeText(currentStep.agentPrompt);
      setCopied(true);
      // Open the agent sidebar
      setExpanded(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [currentStep, setExpanded]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!state.isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          skipWalkthrough();
          break;
        case 'ArrowRight':
        case 'Enter':
          handleNext();
          break;
        case 'ArrowLeft':
          previousStep();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state.isActive, handleNext, previousStep, skipWalkthrough]);

  if (!state.isActive || !currentStep) return null;

  const isFirstStep = state.currentStepIndex === 0;
  const isLastStep = state.currentStepIndex === steps.length - 1;

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none">
      {/* Semi-transparent overlay */}
      <div
        className="absolute inset-0 bg-black/50 pointer-events-auto"
        onClick={(e) => {
          // Only close if clicking the overlay itself
          if (e.target === e.currentTarget) {
            // Do nothing - don't close on overlay click
          }
        }}
      />

      {/* Spotlight highlight */}
      {highlightRect && (
        <div
          className="absolute rounded-lg pointer-events-none"
          style={{
            top: highlightRect.top,
            left: highlightRect.left,
            width: highlightRect.width,
            height: highlightRect.height,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
            border: '2px solid #0066cc',
            borderRadius: '8px',
            animation: 'walkthrough-pulse 2s ease-in-out infinite',
          }}
        />
      )}

      {/* Tooltip */}
      {tooltipPosition && (
        <div
          ref={tooltipRef}
          className="absolute bg-white dark:bg-slate-800 rounded-xl shadow-2xl pointer-events-auto"
          style={{
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            width: 320,
            maxWidth: 'calc(100vw - 32px)',
            animation: 'walkthrough-fade-in 0.3s ease-out',
          }}
        >
          {/* Close button */}
          <button
            onClick={skipWalkthrough}
            className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
            aria-label="Close walkthrough"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Content */}
          <div className="p-5">
            <h3 className="font-semibold text-lg text-slate-900 dark:text-slate-100 pr-6 mb-2">
              {currentStep.title}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-4 leading-relaxed">
              {currentStep.content}
            </p>

            {/* Agent prompt suggestion */}
            {currentStep.agentPrompt && (
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
                      Try this in the Agent Sidebar:
                    </p>
                    <p className="text-sm text-blue-800 dark:text-blue-200 italic">
                      "{currentStep.agentPrompt}"
                    </p>
                  </div>
                  <button
                    onClick={handleCopyPrompt}
                    className="flex-shrink-0 p-1.5 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900 rounded transition-colors"
                    title="Copy to clipboard and open sidebar"
                  >
                    {copied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Progress indicator */}
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs text-slate-500">
                Step {state.currentStepIndex + 1} of {steps.length}
              </span>
              <div className="flex gap-1">
                {steps.map((_, index) => (
                  <div
                    key={index}
                    className={`h-1.5 w-1.5 rounded-full transition-colors ${
                      index === state.currentStepIndex
                        ? 'bg-blue-600'
                        : index < state.currentStepIndex
                        ? 'bg-blue-300'
                        : 'bg-slate-300'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Navigation buttons */}
            <div className="flex items-center justify-between gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={previousStep}
                disabled={isFirstStep}
                className="text-slate-600"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>

              <Button
                size="sm"
                onClick={isLastStep ? endWalkthrough : handleNext}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isLastStep ? (
                  'Finish Tour'
                ) : currentStep.action === 'navigate' ? (
                  <>
                    Go There
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Arrow */}
          <div
            className={`absolute w-3 h-3 bg-white dark:bg-slate-800 transform rotate-45 ${
              tooltipPosition.arrowPosition === 'left'
                ? '-left-1.5 top-1/2 -translate-y-1/2'
                : tooltipPosition.arrowPosition === 'right'
                ? '-right-1.5 top-1/2 -translate-y-1/2'
                : tooltipPosition.arrowPosition === 'top'
                ? '-top-1.5 left-1/2 -translate-x-1/2'
                : '-bottom-1.5 left-1/2 -translate-x-1/2'
            }`}
          />
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes walkthrough-pulse {
          0%, 100% {
            box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5), 0 0 0 0 rgba(0, 102, 204, 0.5);
          }
          50% {
            box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5), 0 0 0 8px rgba(0, 102, 204, 0.2);
          }
        }
        @keyframes walkthrough-fade-in {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

